import { Tooltip } from '@components/atoms/ui-kit/tooltip';
import type { SessionProgressState } from '@utils/types/stt-sessions/stt-sessions';

interface ProgressBarProps {
  pct?: number | null;
  state?: SessionProgressState | null;
  failed?: boolean;
  /**
   * The status this bar stands in for ("Processing", "Queued").
   *
   * The bar replaces that text rather than sitting under it, so this is the
   * only thing left carrying the status to a screen reader or to someone
   * hovering the row. It is not optional in practice.
   */
  label?: string;
}

/** Track geometry. Narrow on purpose — it reads as a chip, not a column rule. */
const TRACK = 'h-1.5 w-20 max-w-full rounded-full overflow-hidden';

/**
 * Whether {@link ProgressBar} will actually draw something for this state.
 *
 * Callers that *replace* the status label with the bar must check this first,
 * or a state the bar declines to draw ("done", or no state at all) leaves the
 * status column empty — worse than the plain label it replaced. Exported so
 * that decision is made from one place instead of each call site guessing
 * which states render.
 */
export function rendersProgressBar(
  state?: SessionProgressState | null
): boolean {
  return !!state && state !== 'done';
}

export function ProgressBar({ pct, state, failed, label }: ProgressBarProps) {
  if (!rendersProgressBar(state)) return null;

  const gradientClass = failed
    ? 'bg-gradient-progress-failed'
    : 'bg-gradient-progress';

  const track = { backgroundColor: 'var(--color-border-soft)' };

  // null pct = nothing reported yet → indeterminate shimmer, never a bar at 0%
  if (state === 'queued' || pct == null) {
    return (
      <Tooltip label={label ?? 'Queued'} placement="top">
        <div
          className={TRACK}
          style={track}
          role="progressbar"
          aria-label={label}
          aria-busy
        >
          <div
            className={`h-full w-1/3 rounded-full ${gradientClass}`}
            style={{
              backgroundSize: '200% 100%',
              animation: 'progress-shimmer 1.5s linear infinite'
            }}
          />
        </div>
      </Tooltip>
    );
  }

  const fill = Math.min(100, Math.max(0, pct));
  const rounded = Math.round(fill);

  // The bar is now the only thing in the cell, so the exact number has to be
  // reachable somehow. Hover is where it lives.
  const readout = label ? `${label} — ${rounded}%` : `${rounded}%`;

  return (
    <Tooltip label={readout} placement="top">
      <div
        className={TRACK}
        style={track}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={rounded}
        aria-label={readout}
      >
        <div
          className={`h-full rounded-full ${gradientClass}`}
          style={{
            width: `${fill}%`,
            // Stale means we have a value but nothing recent to back it up, so
            // it is shown dimmed and, per the spec, deliberately not animated.
            opacity: state === 'stale' ? 0.45 : 1,
            transition: 'width 0.4s ease'
          }}
        />
      </div>
    </Tooltip>
  );
}
